import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const RevealContext = createContext({ forceVisible: false });

export function RevealProvider({ children, forceVisible = false }) {
  return (
    <RevealContext.Provider value={{ forceVisible }}>
      {children}
    </RevealContext.Provider>
  );
}

export function useOnScreen(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold });

    observer.observe(currentRef);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useSectionPhase() {
  const ref = useRef(null);
  const [phase, setPhase] = useState('pre');
  const phaseRef = useRef(phase);
  const yRef = useRef(0);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPhase('in');
      return undefined;
    }

    const el = ref.current;
    if (!el) return undefined;

    const readY = () => window.scrollY || document.documentElement.scrollTop || 0;
    yRef.current = readY();

    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const y = readY();
      const dir = y > yRef.current + 2 ? 'down' : y < yRef.current - 2 ? 'up' : 'none';
      if (dir !== 'none') yRef.current = y;

      const current = phaseRef.current;
      const below = rect.top > vh * 0.82;
      const leavingDown = dir === 'down' && rect.top < vh * 0.14 && rect.bottom > 64;
      const past = rect.bottom < vh * 0.18;
      const inBand = rect.top < vh * 0.72 && rect.bottom > vh * 0.38;

      if (below) {
        if (current !== 'pre') setPhase('pre');
        return;
      }
      if (leavingDown || (past && dir !== 'up')) {
        if (current !== 'leave') setPhase('leave');
        return;
      }
      if (inBand || (dir === 'up' && rect.bottom > vh * 0.28 && rect.top < vh * 0.75)) {
        if (current !== 'in') setPhase('in');
      }
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return [ref, phase];
}

export function Reveal({ children, delay = "delay-0", direction = "up" }) {
  const { forceVisible } = useContext(RevealContext);
  const [ref, isVisible] = useOnScreen(0.1);
  const visible = forceVisible || isVisible;

  let translateClass = "translate-y-12";
  if (direction === "left") translateClass = "translate-x-12";
  if (direction === "right") translateClass = "-translate-x-12";

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${delay} ${
        visible ? "opacity-100 translate-y-0 translate-x-0" : `opacity-0 ${translateClass}`
      }`}
    >
      {children}
    </div>
  );
}
